import { CircularProgress } from '@civilgrid/ui/components';
import * as turf from '@turf/turf';
import { booleanEqual, simplify } from '@turf/turf';
import type { BBox } from 'geojson';
import { useEffect, useState } from 'react';
import { useAsyncRetry } from 'react-use';
import { getAIProjectDescription as apiGetAiProjectDescription } from '~/api/civilsearch';
import FormSection from '~/pages/admin/civilsearch/common/FormSection';
import type { TProject } from './common';
import { Separator } from './Details';
import Export from './Export';
import OpenProject from './OpenProject';
import RefreshAiProjectDescriptionButton from './RefreshAiProjectDescriptionButton';

const getMapScale = (area: number) => {
  if (area > 500) {
    return 1;
  }

  if (area < 50) {
    return 25;
  }

  const t = (area - 50) / (500 - 50);
  return 25 + t * (1 - 25);
};

const getStaticImageUrl = (feature: GeoJSON.Feature, boundingBox: BBox) => {
  const bboxPolygon = turf.bboxPolygon(boundingBox);
  const bboxArea = turf.area(bboxPolygon);
  const scale = getMapScale(bboxArea);
  const bufferedPolygon = turf.transformScale(bboxPolygon, scale);
  const scaledBbox = bufferedPolygon ? turf.bbox(bufferedPolygon) : boundingBox;
  const [p1, p2, p3, p4] = scaledBbox;

  const geojson = encodeURIComponent(
    JSON.stringify(feature, (key, value) =>
      // GeoJSON is sometimes including the `crs` property which is no longer valid as of RFC 7946
      // https://datatracker.ietf.org/doc/html/rfc7946
      key === 'crs' ? undefined : value,
    ),
  );
  const url = `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/geojson(${geojson})/[${p1},${p2},${p3},${p4}]/1000x1000?padding=100&access_token=pk.eyJ1IjoiYWdvcmFtYXBzMjEiLCJhIjoiY2ttZmR2cGltMTRsYzJwa2hyMDFsNmMwNyJ9.RDMHBU_k7vKwWcZVpLKPAw`;
  const urlByteSize = new Blob([url]).size;

  // mapbox max URL length is 8,192 bytes, so if the current url is longer than that we need to simplify the feature
  if (urlByteSize > 8_192) {
    const simplifiedFeature = simplify(feature, {
      highQuality: true,
      tolerance: 0.0005,
    });
    if (booleanEqual(feature, simplifiedFeature))
      throw new Error('Request url too long and feature cannot be simplified further');

    return getStaticImageUrl(simplifiedFeature, boundingBox);
  } else {
    return url;
  }
};

export default function ProjectMap({ project }: { project: TProject }) {
  const [isLoading, setIsLoading] = useState(false);
  const [src, setSrc] = useState<string>('');
  const [aiProjectDescription, setAiProjectDescription] = useState<string>('');

  const getAiProjectDescription = async () => {
    const description = await apiGetAiProjectDescription(project.id);
    setAiProjectDescription(description);
  };

  useAsyncRetry(async () => {
    await getAiProjectDescription();
  }, [project?.id]);

  const getMapImage = async () => {
    if (!project) return;

    const { geometry } = project;
    const boundingBox = turf.bbox(geometry);
    const feature: GeoJSON.Feature = {
      type: 'Feature',
      geometry: geometry,
      properties: {
        stroke: 'black',
        'stroke-width': 4,
        fill: 'black',
        'fill-opacity': 0.25,
      },
    };

    try {
      const url = getStaticImageUrl(feature, boundingBox);
      const img = await fetch(url)
        .then((r) => r.blob())
        .then((blob) => URL.createObjectURL(blob));

      setSrc(img);
    } catch (error) {
      console.error('Fetch error:', error);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    getMapImage();
  }, [project?.id]);

  if (!project) return null;
  if (isLoading) return <CircularProgress indeterminate />;

  const { street, city, state, zipCode } = project.address2;
  const value = `${street}, ${city}, ${state} ${zipCode}`;

  return (
    <div className="h-full grow space-y-4">
      <FormSection title="Export">
        <Export project={project} />
      </FormSection>
      <Separator />
      <FormSection title="Project Area" headerChildren={<OpenProject id={project.id} />}>
        <img src={src} alt="Project area map preview" />
      </FormSection>
      <Separator />
      <FormSection title="AI Project Description (Alpha)">
        <div className="grid grid-cols-2 gap-4">
          {aiProjectDescription ? (
            <div className="whitespace-pre-wrap text-xs flex flex-col gap-2">
              {aiProjectDescription}
              <RefreshAiProjectDescriptionButton
                setProjectDescription={setAiProjectDescription}
                projectId={project.id}
              />
            </div>
          ) : (
            <CircularProgress indeterminate />
          )}
        </div>
      </FormSection>
    </div>
  );
}
